import { format, parseISO } from 'date-fns';
import {
  AddressDataType,
  DriverDataType,
  DriverFieldsType,
  VehicleFieldsType,
} from 'QuoteApp/services/endpoints/GetDetails';
import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';
import { combineStrings } from 'Shared/utils';

// YYYY-MM-DD, allow dash or slash delimiters
const ISORegex = /\d{4}(\/|-)[01]\d(\/|-)[0-3]\d/;

// YYYY-MM-DD or YYYY-**-DD or YYYY-**-** or YYYY-MM-**, allow dash or slash delimiters
export const ISOMaskedRegex = /(\d{4}|\*{4})(\/|-)([01]\d|\*{2})(\/|-)([0-3]\d|\*{2})/;

// MM-DD-YYYY or **-DD-YYYY or **-**-YYYY or MM-**-YYYY, allow dash or slash delimiters
const MDYMaskedRegex = /([01]\d|\*{2})(\/|-)([0-3]\d|\*{2})(\/|-)\d{4}/;

// MM-DD-YYYY, allow dash or slash delimiters, supports a leap year, optimized with chatgpt
const MDYRegex = /^(0[1-9]|[12]\d|3[01])[-/](0[13578]|1[02])[-/](\d{4})$|^(0[1-9]|[12]\d|30)[-/](0[13456789]|1[012])[-/](\d{4})$|^(0[1-9]|1\d|2[0-8])[-/]02[-/](\d{4})$|^29[-/]02[-/](\d{4})$/gm;

const stripInvalidCharacters = (value: string, regex: RegExp) => {
  return value.replace(regex, '');
};

const normalizeDelimiter = (value: string, delimiter: string): string => {
  return value.replace(/\/|-/g, delimiter);
};

const parseFromUnmaskedISO = (value: string): string | void => {
  const normalizedValue = normalizeDelimiter(value, '-');
  try {
    const isoDate = parseISO(normalizedValue);
    return format(isoDate, 'MM/dd/yyyy');
  } catch {
    // parseISO can throw, if so allow attempting the other parse methods
  }
};

export const parseFromMaskedISO = (value: string): string => {
  const normalizedValue = normalizeDelimiter(value, '-');
  const d = normalizedValue.split('-'); // [YYYY, MM, DD]
  return `${d[1]}/${d[2]}/${d[0]}`; // MM/DD/YYYY
};

const parseFromMDY = (value: string): string => {
  return normalizeDelimiter(value, '/');
};

export const maskedMonthDateYearNormalizer = (
  originalValue: string
): string => {
  return monthDateYearNormalizer(originalValue, true);
};

export const monthDateYearNormalizer = (
  originalValue: string,
  masked?: boolean
): string => {
  if (!originalValue) return originalValue;

  const allowedCharactersRegex = masked ? /[^\d/\-*]/g : /[^\d/-]/g; // 0-9, slash, dash, asterisk if masked
  const value = stripInvalidCharacters(originalValue, allowedCharactersRegex);

  if (value.match(ISORegex)) {
    const parsedISO = parseFromUnmaskedISO(value);
    if (parsedISO) return parsedISO;
  }

  if (masked && value.match(ISOMaskedRegex)) {
    return parseFromMaskedISO(value);
  }

  if (value.match(MDYRegex) || (masked && value.match(MDYMaskedRegex))) {
    return parseFromMDY(value);
  }

  // Handle user entered fuzzy MDY
  const splitValues = normalizeDelimiter(value, '/').split('/');
  let valueDigits = [
    splitValues[1]?.length && splitValues[0]?.length === 1
      ? `0${splitValues[0]}`
      : splitValues[0],
    splitValues[2]?.length && splitValues[1]?.length === 1
      ? `0${splitValues[1]}`
      : splitValues[1],
    splitValues[2],
  ].join('');

  const mm = valueDigits.substring(0, 2);
  if (parseInt(mm) > 12 || (mm.includes('*') && parseInt(mm.substring(0, 1)))) {
    // Inject leading month 0 if needed
    // assuming user entered single digit month then started date
    // 31/2 should be 03/12
    valueDigits = `0${valueDigits}`;
  }

  const dd = valueDigits.substring(2, 4);
  if (parseInt(dd) > 31) {
    // Inject leading date 0 if needed
    // assuming user entered single digit month then started date
    // 03/32/020 should be 03/03/2020
    // Known limitation: 1 or 2 will not get a 0 before
    valueDigits = `${valueDigits.substring(0, 2)}0${valueDigits.substring(2)}`;
  }

  const month = valueDigits.substring(0, 2);
  const day = valueDigits.substring(2, 4);
  const year = valueDigits.substring(4, 8);

  // inject slashes, but not trailing since then you can't delete
  // but keep the trailing slash if the user typed it
  const trailingSlash = value[value.length - 1] === '/' ? '/' : '';
  if (!day.length && !year.length) {
    return `${month}${trailingSlash}`;
  } else if (!year.length) {
    return `${month}/${day}${trailingSlash}`;
  }
  return `${month}/${day}/${year}`;
};

export const formatOptions = (
  options?: Array<{ label: string; value: string }>
): Record<string, string> =>
  options
    ? options.reduce(
        (acc, o) => ({
          ...acc,
          [o.value]: o.label,
        }),
        {}
      )
    : {};

export const creditCardNumberNormalizer = (value: string) => {
  if (!value) return value;

  const digits = Array.from(value).filter(char => /\d/.test(char));

  return [
    digits.slice(0, 4),
    digits.slice(4, 8),
    digits.slice(8, 12),
    digits.slice(12, 16),
  ]
    .filter(chunk => chunk.length > 0)
    .map(chunk => chunk.join(''))
    .join(' ');
};

export const expirationDateNormalizer = (value: string) => {
  if (!value) return value;

  const digits = Array.from(value).filter(char => /\d/.test(char));
  return [digits.slice(0, 2), digits.slice(2, 4)]
    .filter(chunk => chunk.length > 0)
    .map(chunk => chunk.join(''))
    .join('/');
};

export const ssnNormalizer = (value: string) => {
  if (!value) return value;

  const digits = Array.from(value).filter(char => /\d/.test(char));
  return [digits.slice(0, 3), digits.slice(3, 5), digits.slice(5, 9)]
    .filter(chunk => chunk.length > 0)
    .map(chunk => chunk.join(''))
    .join('-');
};

export const formatDriverDisplay = (
  driver: DriverFieldsType,
  config: GetConfigResponseType
) =>
  combineStrings([
    driver.firstName,
    driver.middleName,
    driver.lastName,
    config.drivers.personalInfo.suffix[driver.suffix],
  ]);

export const formatDriverDisplayFromDriverData = (
  driver: DriverDataType,
  config: GetConfigResponseType
) =>
  combineStrings([
    driver.firstName.value,
    driver.middleName.value,
    driver.lastName.value,
    config.drivers.personalInfo.suffix[driver.suffix.value],
  ]);

export const formatVehicleDisplay = (vehicle: VehicleFieldsType) =>
  combineStrings([vehicle.year, vehicle.make, vehicle.model]);

export const formatAddressDisplay = (
  address: AddressDataType,
  includeZip: boolean = true
): string => {
  const { address1, address2, city, state, zip } = address;
  return combineStrings([
    `${combineStrings([
      address2.value ? `${address1.value},` : address1.value,
      address2.value,
    ])},`,
    `${city.value},`,
    state.value,
    includeZip ? zip.value : '',
  ]);
};

export const formatMailingAddressDisplay = (
  address: AddressDataType,
  includeZipCode: boolean = true
): string => {
  const { address1, address2, city, state, zip } = address;

  if (
    !isMailingAddressValid(address, [
      'address1',
      'city',
      'state',
      ...(includeZipCode ? ['zip' as keyof AddressDataType] : []),
    ])
  ) {
    return '';
  }

  return combineStrings([
    `${combineStrings([
      address2.value ? `${address1.value},` : address1.value,
      address2.value,
    ])},`,
    `${city.value},`,
    state.value,
    includeZipCode ? zip.value : '',
  ]);
};

export const isMailingAddressValid = (
  address: AddressDataType,
  requiredFields: Array<keyof AddressDataType>
): boolean => {
  return requiredFields.every(field => {
    const value = address[field];

    if (typeof value === 'object' && 'value' in value) {
      return value.value && value.value !== '';
    }

    return value && value !== '';
  });
};
